import { ProductCategory, ProductSubcategory } from 'api/types/product';
import * as images from './images';
import { EPriceType } from '../ProductBaseForm/constants';
import { IProductPrice } from 'api/product_sdk';

export interface SuggestedProduct {
  id: number;
  name: string;
  price: string;
  preorderBy?: number;
  requiresApproval?: boolean;
  description: string;
  preview: string;
  instructions: string;
  quantity?: number;
  hasQuantity?: boolean;
  image: string;
  category: ProductCategory;
  subcategory: ProductSubcategory;
  priceType: EPriceType;
  // Special product types, distinguished from other
  isEarlyCheckin?: boolean;
  isLateCheckout?: boolean;
  productPrices?: IProductPrice[];
  hideFromQuickAdd?: boolean;
}

export const DEFAULT_PRODUCT_DESCRIPTION = `Add all the details here!
Note:
\u2022 If you do not list a cancellation policy with a time frame, your guest will be entitled to a full refund if service is cancelled prior to fulfillment.
\u2022 Your own vendors must have a cancellation and insurance policy.
\u2022 The Host Co is not responsible or liable for services you list individually.
`;
export const DEFAULT_PRODUCT_CANCELLATION_POLICY = `Add your cancellation details!
Note:
\u2022 If you do not list a cancellation policy with a time frame, your guest will be entitled to a full refund if service is cancelled prior to fulfillment.
\u2022 Your own vendors must have a cancellation and insurance policy.
\u2022 The Host Co is not responsible or liable for services you list individually.
`;

export const DEFAULT_PRODUCT_AIRBNB_INSTRUCTIONS = `Complete this payment on the Airbnb platform:\n
\u0031. Go to https://www.airbnb.com/resolutions, or click "details" on this reservation in Airbnb.
\u0032. Click "send money"
\u0033. Enter the amount listed in this tile. \n\nThank you!`;

export const PRODUCT_DESCRIPTIONS = {
  midStayClean: `Want an extra clean during your stay? We'll make your bed, put out fresh towels and straighten up your room.  
  Note:
  \u2022 If you do not list a cancellation policy with a time frame, your guest will be entitled to a full refund if service is cancelled prior to fulfillment.
  \u2022 Your own vendors must have a cancellation and insurance policy.
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  poolHeatingPerDay: `
  Want a heated pool? This is for one day of pool heating, so please increase the quantity for the number of days you're staying. Enjoy!
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  hotTubHeating: `
  Want to use the hot tub? This is for one day of hot tub use, so please increase the quantity for the number of days you're staying. Must include cancellation policy or your guests will be entitled to a full refund upon cancellation.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  firewood: `Don't drive into town. We have bundles of firewood here.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  romancePackage: `
  Having a romantic weekend away or an anniversary?
  Our romance package includes a bouquet of flowers, a box of chocolates and rose petals on the bed when you walk in.
  Perfect for a romantic weekend. Must cancel 72 hours in advance for a full refund. 
  NOTE:  
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  parkingPass: `Use our parking so you don't have to park on the street. Must purchase one per day of your stay. Cancellation policy: Must cancel 5 days in advance for a full refund.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  packNPlayRental: `
  Rent our pack n' play with washable bedding and pillows. Price is per stay. Must cancel prior to arrival for full refund.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  bikeRental: `Rent our bikes during your stay. Rental price is per stay for one bicycle. If your rental requires a waiver, we recommend hyperlinking to that waiver here and putting this item on request and approve.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  laundryRoom: `Use of our laundry room including detergent and dryer sheets.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  toothbrushAndPaste: `One tube of toothpaste and one toothbrush.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  brokenWineGlass: `Break a wine glass?  That's ok, you can pay for it here.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  breakfast: `We serve breakfast with advanced notice. Breakfast includes (INSERT MENU HERE). Must cancel 48 hours in advance for a full refund.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  extraCoffee: `We provide one morning's worth of coffee. Want extra for your stay? We'll provide an entire bag of coffee and feel free to take home the rest!
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  extraLinens: `Extra sheets, blankets and pillows for the hide-a-bed and extra towels as well.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  luggageStorage: `Drop off your luggage 4 hours in advance or leave your luggage behind for an additional 4 hours.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  extraGuest: `Got an extra guest? You can request to pay for your extra guest here. If you have more than one extra guest, be sure to buy multiples!
  Note:
  \u2022 This product is not approved until the guest receives the "Request Approved" email from The Host Co.
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  earlyCheckIn: `Want to check-in early? We'll let you check-in two hours before the scheduled check-in time! 
  Note:
  \u2022 This product is not approved until the guest receives the "Request Approved" email from The Host Co. 
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  petFee: `Want to bring your pet along? Request to bring your pet(s) here! This applies to up to 2 dogs under (x) pounds only.
  Note:
  \u2022 This product is not approved until the guest receives the "Request Approved" email from The Host Co.
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  lateCheckOut: `Need some time to sleep in? Go for it! With late check-out we'll give you two more hours from the scheduled checkout time before you have to go!
  Note:
  \u2022 This product is not approved until the guest receives the "Request Approved" email from The Host Co.
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  earPlugs: `One pair of foam earplugs.
  Note:
  \u2022 This is an owner-supplied service so please only offer things you can personally provide to guests when purchased.
  `,
  gratuity: `Love your stay? Leave a tip! Your cleaner will receive all proceeds and get an alert at the time of tipping transaction. Thank you!`,
  tipTheCleaner: `Want to tip the cleaner extra? Send them a tip here!`,
  returnPostage: `Want an item mailed back to you?  You can pay for the postage here.  Please don't send until amount has been agreed upon with host.`,
  eventsOrFilming: `If you've worked out an event or filming with your host, you can send those funds here.  Please don't send until amount has been agreed upon with host.`,
  damages: `Break something?  Accidents happen.  This is a quick and easy way to send the funds to your host without having to involve the bookings platform. Please don't send until amount has been agreed upon with host.`,
  testPurchase: `See what your guest sees with a zero dollar test purchase. This includes request and approve, a calendar for date selection, and email receipts to both you and your (test) guest. Don't forget to set this tile to "inactive" after testing.`,
  shipItemHome: `We will pack and ship an item back to you. Postage will be requested after this items ships.`,
  errandService:
    'Let us stock your pantry with pre-ordered groceries or drop off flowers for your romantic weekend. Whatever you need! Most tasks can be accomplished in under an hour, but if you have a lot of errands for us, purchase multiple hours and we’ll make sure you have the perfect vacation!  You order the items, pay for them and set us as the pickup person.  Must cancel 24 hours in advance for a full refund.',
};

export const suggestedProductsData: SuggestedProduct[] = [
  {
    id: 22,
    name: 'Return Postage',
    price: '0',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    description: PRODUCT_DESCRIPTIONS.returnPostage,
    preview: 'Mail an item back to a guest.',
    instructions: '',
    image: images.postage,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.SomethingElse,
    priceType: EPriceType.VARIABLE,
  },
  {
    id: 25,
    name: 'Test Purchase',
    price: '0',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    requiresApproval: true,
    description: PRODUCT_DESCRIPTIONS.testPurchase,
    preview: 'See how your store works with a $0 sale.',
    instructions: '',
    image: images.testPurchase,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.SomethingElse,
    priceType: EPriceType.STATIC,
  },
  {
    id: 26,
    name: 'Ship an Item Home - Plus Postage Cost',
    price: '35',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    requiresApproval: true,
    description: PRODUCT_DESCRIPTIONS.shipItemHome,
    preview:
      'We will pack and ship an item back to you. Postage will be requested after this items ships.',
    instructions: '',
    image: images.shipItemHome,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.SomethingElse,
    priceType: EPriceType.STATIC,
  },
  {
    id: 27,
    name: 'Errand Service',
    price: '75',
    preorderBy: 1,
    quantity: 0,
    hasQuantity: false,
    requiresApproval: true,
    description: PRODUCT_DESCRIPTIONS.errandService,
    preview:
      'Let us stock your pantry with pre-ordered groceries or drop off flowers for your romantic weekend.',
    instructions:
      "We’ll reach out for details. You'll need to let us know what you ordered and where we can pick it up for you to have in the rental prior to your stay.",
    image: images.errandService,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 1,
    name: 'Mid-stay Clean',
    price: '100',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.midStayClean,
    preview: 'Additional Clean',
    instructions: '',
    image: images.midweekCleaning,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 2,
    name: 'Pool Heating Per Day',
    price: '75',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.poolHeatingPerDay,
    preview: 'Per day or per stay, allow your guest to add pool heating',
    instructions: '',
    image: images.poolHeating,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 3,
    name: 'Use of Hot Tub/ Hot Tub Heating',
    price: '30',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.hotTubHeating,
    preview: 'If hot-tub heating is an add-on, have your guest request it here',
    instructions: '',
    image: images.hotTubHeating,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 4,
    name: 'Firewood',
    price: '20',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.firewood,
    preview: 'One bundle of firewood',
    instructions: '',
    image: images.firewood,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GoodsAndEssentials,
    priceType: EPriceType.STATIC,
  },
  {
    id: 5,
    name: 'Romance Package',
    price: '125',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.romancePackage,
    preview: 'Create a romantic package for guests',
    instructions: '',
    image: images.romancePackage,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GoodsAndEssentials,
    priceType: EPriceType.STATIC,
  },
  {
    id: 6,
    name: 'Parking Pass',
    price: '25',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.parkingPass,
    preview: 'Let your guest reserve parking per day or per space',
    instructions: '',
    image: images.parkingPass,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.Rentals,
    priceType: EPriceType.STATIC,
  },
  {
    id: 7,
    name: 'Pack N Play Rental',
    price: '40',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.packNPlayRental,
    preview: "Rent a pack n' play with washable bedding",
    instructions: '',
    image: images.packNPlayRental,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.Rentals,
    priceType: EPriceType.STATIC,
  },
  {
    id: 8,
    name: 'Bike Rental',
    price: '25',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.bikeRental,
    preview: 'Per day or per stay bike rental',
    instructions: '',
    image: images.bikeRental,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.Rentals,
    priceType: EPriceType.STATIC,
    hideFromQuickAdd: true,
  },
  {
    id: 9,
    name: 'Use of Laundry Room',
    price: '20',
    preorderBy: 0,
    description: PRODUCT_DESCRIPTIONS.laundryRoom,
    preview: 'Grant access for guests to do their own laundry.',
    instructions: '',
    image: images.laundryService,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 10,
    name: 'Toothbrush and Toothpaste',
    price: '10',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.toothbrushAndPaste,
    preview: 'Forgot yours? Buy them here!',
    instructions: '',
    image: images.toothBrushAndPaste,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GoodsAndEssentials,
    priceType: EPriceType.STATIC,
  },
  {
    id: 11,
    name: 'Broken Wine Glass',
    price: '10',
    preorderBy: 0,
    description: PRODUCT_DESCRIPTIONS.brokenWineGlass,
    preview: 'A great way to cover broken glassware',
    instructions: '',
    image: images.brokenWineGlass,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 12,
    name: 'Breakfast (per person)',
    price: '25',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.breakfast,
    preview: 'Serve your guests breakfast? Let them order here.',
    instructions: '',
    image: images.breakfast,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.FoodAndBeverage,
    priceType: EPriceType.STATIC,
  },
  {
    id: 13,
    name: 'Extra coffee',
    price: '15',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.extraCoffee,
    preview: 'Extra coffee for guests who need it!',
    instructions: '',
    image: images.extraCoffee,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.FoodAndBeverage,
    priceType: EPriceType.STATIC,
  },
  {
    id: 14,
    name: 'Extra Linens',
    price: '25',
    preorderBy: 0,
    description: PRODUCT_DESCRIPTIONS.extraLinens,
    preview: 'Want to use the hide-a-bed? Need extra towels? Extra linens charge.',
    instructions: '',
    image: images.extraLinens,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.GuestServices,
    priceType: EPriceType.STATIC,
  },
  {
    id: 15,
    name: 'Luggage Storage',
    price: '25',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.luggageStorage,
    preview: 'Leave your bags at the house for extra time or drop them off early',
    instructions: '',
    image: images.luggageStorage,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.Rentals,
    priceType: EPriceType.STATIC,
  },
  {
    id: 16,
    name: 'Ear Plugs',
    price: '5',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.earPlugs,
    preview: 'When you need them, you need them!',
    instructions: '',
    image: images.earPlugs,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.GoodsAndEssentials,
    priceType: EPriceType.STATIC,
  },
  {
    id: 17,
    name: 'Request Extra Guest',
    price: '50',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.extraGuest,
    preview: 'Have your guest send in requests and updates via this tile',
    instructions: '',
    image: images.extraGuest,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.AddOns,
    priceType: EPriceType.STATIC,
  },
  {
    id: 18,
    name: 'Request Early Check-In',
    price: '30',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.earlyCheckIn,
    preview: 'Know you can arrive early!',
    instructions: '',
    image: images.earlyCheckin,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.AddOns,
    isEarlyCheckin: true,
    priceType: EPriceType.MULTIPLE,
    productPrices: [
      { id: null, timeLabel: '14:00:00', price: 30, arrangement: 0, isVariablePrice: false },
    ],
  },
  {
    id: 19,
    name: 'Pet Request',
    price: '35',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.petFee,
    preview: 'Cover one or more pets',
    instructions: '',
    image: images.petRequest,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.AddOns,
    priceType: EPriceType.STATIC,
  },
  {
    id: 20,
    name: 'Request Late Check-Out',
    price: '30',
    preorderBy: 2,
    description: PRODUCT_DESCRIPTIONS.lateCheckOut,
    preview: 'Sleep in!',
    instructions: '',
    image: images.lateCheckout,
    category: ProductCategory.PO,
    subcategory: ProductSubcategory.AddOns,
    isLateCheckout: true,
    priceType: EPriceType.MULTIPLE,
    productPrices: [
      { id: null, timeLabel: '13:00:00', price: 30, arrangement: 0, isVariablePrice: false },
    ],
  },
  {
    id: 21,
    name: 'Tip the Cleaner',
    price: '0',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    description: PRODUCT_DESCRIPTIONS.tipTheCleaner,
    preview: 'Leave a tip!',
    instructions: '',
    image: images.tipping,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.SomethingElse,
    priceType: EPriceType.VARIABLE,
  },
  {
    id: 24,
    name: 'Damages',
    price: '0',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    description: PRODUCT_DESCRIPTIONS.damages,
    preview: 'Guests can cover damages here.',
    instructions: '',
    image: images.damages,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.SomethingElse,
    priceType: EPriceType.VARIABLE,
  },
  {
    id: 23,
    name: 'Event or Filming',
    price: '0',
    preorderBy: 0,
    quantity: 0,
    hasQuantity: false,
    description: PRODUCT_DESCRIPTIONS.eventsOrFilming,
    preview: 'Add-ons for events and filming.',
    instructions: '',
    image: images.eventsOrFilming,
    category: ProductCategory.IH,
    subcategory: ProductSubcategory.AddOns,
    priceType: EPriceType.VARIABLE,
  },
];

export const parseSuggestedProductsToCardItems = (
  selectedSubcategory?: ProductSubcategory,
  isQuickAdd = false,
) => {
  return suggestedProductsData
    .filter(({ subcategory: productSubcategory, hideFromQuickAdd }) => {
      if (isQuickAdd && hideFromQuickAdd) {
        return false;
      }
      return !selectedSubcategory ? true : productSubcategory === selectedSubcategory;
    })
    .map(
      ({
        id,
        name,
        description,
        preview,
        image,
        subcategory,
        priceType,
        isEarlyCheckin,
        isLateCheckout,
        productPrices,
      }) => ({
        id,
        title: name,
        description,
        preview,
        image,
        subheader: subcategory,
        priceType,
        isEarlyCheckin,
        isLateCheckout,
        productPrices,
      }),
    );
};

export default suggestedProductsData;
